import { useModuleBEM } from "@b2bportal/core-themes";
import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@b2bportal/core-ui/overridable";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import styles from "./styles.module.scss";

export type PricingMode = "fixed" | "flex";

export interface PricingExplanationDialogProps extends DialogProps {
  className?: string;
  mode: PricingMode;
  cashPer100Points: number;
  /**
   * Cash value of 100 points when redeemed against flight add-ons (seats,
   * CFAR). Only defined for Emerald cardholders; when defined, the copy
   * mentions add-ons and their redemption rate.
   */
  addOnsCashPer100Points?: number;
}

export const PricingExplanationDialog = ({
  className,
  open,
  onClose,
  onOpen,
  children,
  mode,
  cashPer100Points,
  addOnsCashPer100Points,
  ...props
}: PricingExplanationDialogProps): JSX.Element => {
  const { t, formatFiatCurrency, brand } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "PricingExplanationDialog");

  const title =
    mode === "fixed"
      ? t("core-flights.pricingExplanation.fixed.title")
      : t("core-flights.pricingExplanation.flex.title");

  const hasAddOns = addOnsCashPer100Points !== undefined;
  const addOnsRate = hasAddOns
    ? {
        points: 100,
        price: formatFiatCurrency({
          value: addOnsCashPer100Points,
          currencyCode: brand.currency.code,
        }),
      }
    : undefined;

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen} {...props}>
      <DialogTrigger>{children}</DialogTrigger>

      <DialogContent
        className={clsx(block, className)}
        title={title}
        bottomFixedContent={
          <button
            type="button"
            className={clsx(cn("cta-button"), "cta-button")}
            onClick={onClose}
          >
            {t("done")}
          </button>
        }
      >
        {mode === "fixed" ? (
          <>
            <p className={cn("paragraph")}>
              {t("core-flights.pricingExplanation.fixed.paragraph1")}
            </p>
            <p className={cn("paragraph")}>
              {addOnsRate ? (
                <Trans
                  i18nKey="core-flights.pricingExplanation.fixed.paragraph2AddOns"
                  components={[<strong className={cn("bold-text")} />]}
                  values={addOnsRate}
                />
              ) : (
                <Trans
                  i18nKey="core-flights.pricingExplanation.fixed.paragraph2"
                  components={[<strong className={cn("bold-text")} />]}
                  values={{
                    points: 100,
                    price: formatFiatCurrency({
                      value: cashPer100Points,
                      currencyCode: brand.currency.code,
                    }),
                  }}
                />
              )}
            </p>
          </>
        ) : (
          <>
            <p className={cn("paragraph")}>
              <Trans
                i18nKey="core-flights.pricingExplanation.flex.paragraph1"
                components={[<strong className={cn("bold-text")} />]}
                values={{
                  points: 100,
                  price: formatFiatCurrency({
                    value: cashPer100Points,
                    currencyCode: brand.currency.code,
                  }),
                }}
              />
            </p>
            <p className={cn("paragraph")}>
              {t("core-flights.pricingExplanation.flex.paragraph2")}
            </p>
            {addOnsRate && (
              <p className={cn("paragraph")}>
                {t(
                  "core-flights.pricingExplanation.flex.paragraph3AddOns",
                  addOnsRate
                )}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
